import React, { useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ReactFlow blocks wheel-zoom and panning when the wheel/pan event's target sits inside an element
// carrying its noWheelClassName / noPanClassName (default 'nowheel' / 'nopan'). The playground wraps
// every widget body in a `.nowheel` element so scrolling over a widget never zooms the OUTER playground
// canvas — but that `.nowheel` is also an ancestor of THIS nested canvas, so with the default class our
// own wheel-zoom would be suppressed too (the check is an unbounded `closest('.nowheel')`). Giving this
// canvas its own private no-wheel class decouples it: the outer `.nowheel` no longer matches here, so the
// mission graph zooms on wheel/pinch while the outer playground still ignores the same scroll. Node
// dragging is unaffected — that check is DOM-bounded to this canvas, so the wrapper's `nodrag` never
// reaches our nodes.
const NO_WHEEL_CLASS = 'mc-graph-nowheel';

// Every <Background> builds its SVG pattern id as `pattern-${rfId}` + its own optional `id` prop, and
// rfId defaults to '1' for EVERY ReactFlow that is not given one. Two GraphCanvas instances therefore
// both emitted <pattern id="pattern-1">, and each one's <rect fill="url(#pattern-1)"> resolved to
// whichever came FIRST in the document — so the cells surface's workflow preview was painted with the
// mission graph's pattern, carrying the mission graph's transform, and its dots could not respond to
// its own zoom. The app's three other Background call sites each pass a unique id for exactly this
// reason (playground-background / preview-background / workflow-background); this canvas is shared, so
// it mints one per instance instead of naming one.
let canvasSeq = 0;

/**
 * GraphCanvasInner — controlled ReactFlow canvas for Mission Control surfaces.
 * Intentionally simpler than the generic graph widget's canvas: no layout
 * computation, no subset mode, no delete-key interception. Surfaces that need
 * interaction (selection, drag-to-connect, node dragging) pass the optional handlers
 * below; absent props keep the canvas static, which is how the read-only workflows
 * surface uses it. The canvas defines the mission dependency-edge arrow markers
 * (mc-graph-edge-arrow / -selected) and accepts an optional `edgeTypes` map; the cells
 * surface passes a 'dependsOn' edge type that references those markers, while surfaces
 * that omit `edgeTypes` (e.g. workflows) fall back to ReactFlow's built-in edge types.
 * Node dragging is controlled per-node (node.draggable), not at the canvas level.
 *
 * The canvas always pans (drag the empty pane) and zooms (wheel + pinch); multi-select
 * is disabled (single selection only) so a background drag always pans instead of
 * rubber-band-selecting.
 *
 * Props:
 *   nodes            — ReactFlow node array
 *   edges            — ReactFlow edge array
 *   nodeTypes        — custom node type map (optional; falls back to ReactFlow defaults)
 *   edgeTypes        — custom edge type map (optional; falls back to ReactFlow defaults)
 *   connectionMode   — 'strict' (default) or 'loose'; 'loose' lets either of a cell's two handles
 *                      initiate a depends_on drag, which the mission graph relies on
 *   onNodeClick      — (node) => void
 *   onNodeDoubleClick — (node) => void; the event is stopped before it reaches the zoom pane, so
 *                      opening a node does not also zoom the canvas underneath it
 *   onEdgeClick      — (edge) => void; fired when an edge is clicked (select)
 *   onPaneClick      — () => void; fired when the empty canvas is clicked (deselect)
 *   onConnect        — (connection) => void; when provided, nodesConnectable is enabled
 *   fitView          — boolean (default true)
 *   onNodesChange    — (changes) => void; forwarded to ReactFlow for controlled-node interaction
 *   onEdgesChange    — (changes) => void; forwarded to ReactFlow for controlled-edge interaction
 *   onNodeDragStart  — (node) => void; fired with the dragged node as a drag begins. ReactFlow selects
 *                      that node on mousedown, so this is where a surface driving selection of its own
 *                      hears about it — without it the canvas and the surface disagree for the length
 *                      of the gesture, and the next re-seed resolves the disagreement in favour of the
 *                      stale one. Observing a drag does not enable one; see onNodeDragStop.
 *   onNodeDragStop   — (node) => void; fired with the dragged node once a drag ends (persist position)
 *   zoomOnScroll     — boolean (default true); pass false so wheeling over an embedded canvas scrolls the
 *                      surrounding panel instead of trapping the wheel to zoom (read-only workflow graph)
 *   showControls     — boolean (default true); pass false to hide the zoom Controls on a small static graph
 *   fitViewOptions   — ReactFlow fitViewOptions (default { maxZoom: 1.1, padding: 0.2 }); a lower maxZoom
 *                      keeps a small graph zoomed out so the whole flow stays visible
 *   minZoom          — ReactFlow minimum zoom (default undefined → ReactFlow's 0.5); lower it so fitView
 *                      can zoom further out to fit a wide flow entirely
 *   showBackground   — boolean (default true); pass false to drop the dotted canvas background
 *   selectable       — boolean (default true); pass false for a canvas that SHOWS a state rather
 *                      than offering a choice, so a click cannot move the mark
 *   attribution      — boolean (default true); pass false on a preview too small to carry the badge
 */
function GraphCanvasInner({
  nodes, edges, nodeTypes, edgeTypes, connectionMode, onNodeClick, onNodeDoubleClick, onEdgeClick,
  onPaneClick, onConnect, fitView, onNodesChange, onEdgesChange, onNodeDragStart, onNodeDragStop,
  zoomOnScroll, showControls, fitViewOptions, minZoom, showBackground, selectable, attribution,
}) {
  // Stable for the life of this instance: assigned on first render, never recomputed.
  const backgroundIdRef = useRef(null);
  if (backgroundIdRef.current === null) {
    canvasSeq += 1;
    backgroundIdRef.current = `-mc-graph-bg-${canvasSeq}`;
  }

  const handleNodeClick = (_event, node) => {
    if (onNodeClick) onNodeClick(node);
  };

  // ReactFlow's zoom pane binds its own dblclick, and a node's event bubbles to it — so opening a
  // cell would leave the canvas zoomed in behind the detail view, waiting for you when you came
  // back. Stopping it here keeps double-click-to-zoom on the empty canvas, where it belongs.
  const handleNodeDoubleClick = (event, node) => {
    if (event && event.stopPropagation) event.stopPropagation();
    if (onNodeDoubleClick) onNodeDoubleClick(node);
  };

  const handleEdgeClick = (_event, edge) => {
    if (onEdgeClick) onEdgeClick(edge);
  };

  const handleConnect = (connection) => {
    if (onConnect) onConnect(connection);
  };

  const handleNodeDragStart = (_event, node) => {
    if (onNodeDragStart) onNodeDragStart(node);
  };

  const handleNodeDragStop = (_event, node) => {
    if (onNodeDragStop) onNodeDragStop(node);
  };

  const connectable = !!onConnect;
  // A drag with nowhere to land is a lie: the node follows the pointer, the move is never persisted,
  // and the next re-seed from the store snaps it back. Draggability follows its handler, the same
  // way connectability follows onConnect — which is what "absent props keep the canvas static"
  // above already promised, and what makes a read-only mission map read-only.
  const draggable = !!onNodeDragStop;
  // A canvas that shows a state rather than offering a choice. The mission map's selection IS the
  // point of it, so this defaults to on; a workflow PREVIEW marks the step the cell is on, and a
  // click that moved that mark would be reporting something untrue about the run.
  const canSelect = selectable !== false;
  // ReactFlow's own badge. It belongs on a canvas somebody WORKS in — the mission map, the editor,
  // the graph widget — and not on a thumbnail a few centimetres tall, where it is a third of the
  // height and names a library the reader is not using. Default on; a preview opts out.
  const proOptions = attribution === false ? { hideAttribution: true } : undefined;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Filled arrowhead markers for the mission dependency edge; the selected variant recolours with
          the edge. Copied from the generic graph widget's canvas (widgets/graph/canvas.js) so the
          arrow matches it exactly — bigger than the default marker and clipped to touch the node. */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
        <defs>
          {[
            { id: 'mc-graph-edge-arrow', color: '#9aa3af' },
            { id: 'mc-graph-edge-arrow-selected', color: '#3d4756' },
          ].map(m => (
            <marker
              key={m.id}
              id={m.id}
              markerWidth="11.2"
              markerHeight="5.6"
              viewBox="0 0 16 8"
              orient="auto"
              refX="16"
              refY="4"
              markerUnits="userSpaceOnUse"
            >
              {/* Filled, elongated (≈2:1 long-to-wide) arrowhead. */}
              <path d="M0,0 L16,4 L0,8 Z" fill={m.color} />
            </marker>
          ))}
        </defs>
      </svg>
      <ReactFlow
        nodes={nodes || []}
        edges={edges || []}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={connectionMode}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={onNodeDoubleClick ? handleNodeDoubleClick : undefined}
        onEdgeClick={onEdgeClick ? handleEdgeClick : undefined}
        onPaneClick={onPaneClick}
        onConnect={connectable ? handleConnect : undefined}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStart={onNodeDragStart ? handleNodeDragStart : undefined}
        onNodeDragStop={onNodeDragStop ? handleNodeDragStop : undefined}
        fitView={fitView !== false}
        fitViewOptions={fitViewOptions || { maxZoom: 1.1, padding: 0.2 }}
        minZoom={minZoom}
        deleteKeyCode={null}
        nodesConnectable={connectable}
        nodesDraggable={draggable}
        elementsSelectable={canSelect}
        panOnDrag
        zoomOnScroll={zoomOnScroll !== false}
        zoomOnPinch
        noWheelClassName={NO_WHEEL_CLASS}
        multiSelectionKeyCode={null}
        selectionKeyCode={null}
        selectionOnDrag={false}
        attributionPosition="bottom-right"
        proOptions={proOptions}
      >
        {showControls !== false && <Controls showInteractive={false} />}
        {showBackground !== false && (
          <Background id={backgroundIdRef.current} variant={BackgroundVariant.Dots} gap={12} size={1} />
        )}
      </ReactFlow>
    </div>
  );
}

/**
 * GraphCanvas — wraps the inner canvas in a ReactFlowProvider.
 */
function GraphCanvas(props) {
  return (
    <ReactFlowProvider>
      <GraphCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default GraphCanvas;
